// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

export interface DivisionSowTemplate {
  divisionId: string;
  divisionName: string;
  tradeName: string;
  scopeItems: string[];
  exclusions: string[];
  referenceSpecs: string[];
  submittals: string[];
  testingInspection: string[];
  laborCategories: string[];
}

export const STANDARD_MISC_ITEMS: string[] = [
  "Pre-purchase of critical long-lead materials is included in the base bid. All deposits will be reimbursed to this contractor.",
  "Product data submittals will be provided by this contractor within 2 weeks of release.",
  "Shop drawing submittals, if any, will be provided within 2 weeks of release.",
  "Hoisting and rigging required for this contractor's work.",
  "Remove all trash and debris generated by this operation to the dumpster provided by others.",
  "This contractor shall not publish or share drawings, photos, videos, or similar content with any person not party to this agreement without authorization.",
  "All warranties commence upon acceptance of the work by the Owner at substantial completion. Warranties shall be for a minimum 2-year term from substantial completion; extended manufacturer warranties shall apply where specified.",
  "Upon award, this contractor will provide a submittal schedule listing all proposed submittal items by CSI section within 5 business days of contract execution.",
  "All work shall be performed in accordance with applicable building codes and ordinances governing the project location.",
  "This contractor shall verify all preceding work for correctness before proceeding. Any discrepancies shall be reported to the Contractor in writing immediately. Trade Contractor shall not construct over faulty work.",
  "This contractor shall participate in all coordination meetings as scheduled by Contractor, including BIM/MEP coordination sessions where applicable.",
  "A competent superintendent shall be on-site at all times during the performance of this work.",
  "Price is valid for the life of the project.",
];

export const SOW_TEMPLATES: Record<string, DivisionSowTemplate> = {
  div1: {
    divisionId: "div1",
    divisionName: "Division 01 — General Requirements",
    tradeName: "General Contractor",
    scopeItems: [
      "Furnish and install all temporary construction fencing, hoarding, and pedestrian protection barriers as required by project phasing and local ordinances.",
      "Provide and maintain all temporary utilities including power, water, telephone/data, and temporary lighting for the duration of construction.",
      "Establish and maintain a temporary construction office, tool storage, and sanitary facilities sufficient for the project workforce.",
      "Develop and implement a project-specific Construction Safety Plan in compliance with OSHA 29 CFR 1926 and submit for Owner review prior to mobilization.",
      "Develop and maintain a project schedule in Primavera P6 or MS Project CPM format; update and distribute bi-weekly throughout the project duration.",
      "Conduct and document weekly progress meetings; distribute meeting minutes to all project stakeholders within 48 hours.",
      "Establish and maintain a project document control system; manage all RFIs, submittals, and change order logs.",
      "Furnish and install all temporary signage, project identification signage, and safety signage per OSHA and Owner requirements.",
      "Provide site security, including temporary lighting, locks, and access control for the duration of the project.",
      "Develop and implement a Construction Waste Management Plan targeting a minimum 75% diversion rate from landfill; provide monthly reporting.",
      "Prepare and submit a Stormwater Pollution Prevention Plan (SWPPP) and implement all required BMPs throughout construction.",
      "Coordinate and manage Special Inspections Program (SIP) with Owner-designated special inspector; maintain inspection log.",
      "Prepare Substantial Completion punch list documentation and coordinate Owner/Architect final walk-through.",
      "Compile and deliver all closeout documentation including O&M manuals, as-built drawings, warranties, attic stock, commissioning reports, and training records.",
      "Provide project-specific safety orientation for all personnel entering the site; maintain sign-in logs.",
    ],
    exclusions: [
      "Owner-furnished equipment (OFE) procurement, delivery coordination, and installation unless specifically noted.",
      "Permit fees, impact fees, utility connection fees, and tap fees (carried under Owner's allowance unless directed otherwise).",
      "Geotechnical investigation, environmental testing, and third-party inspections beyond the Special Inspections Program.",
      "Architect/Engineer design fees and structural peer review.",
      "Owner-direct contracts for FF&E, IT/AV, and specialty equipment.",
      "Offsite storage beyond 30 days for Owner-furnished materials.",
      "Hazardous material abatement unless a separate allowance is included herein.",
    ],
    referenceSpecs: [
      "OSHA 29 CFR 1926 — Safety and Health Regulations for Construction",
      "ACI 117 — Specification for Tolerances for Concrete Construction",
      "ASTM E2026 / E2557 — Standard Guide for Seismic Risk Assessment of Buildings",
      "IBC — International Building Code (current adopted edition)",
      "NFPA 241 — Standard for Safeguarding Construction, Alteration, and Demolition Operations",
      "EPA 40 CFR Part 61 — National Emission Standards for Hazardous Air Pollutants (NESHAP)",
    ],
    submittals: [
      "Construction Safety Plan (site-specific)",
      "CPM Project Schedule — baseline and monthly updates",
      "Submittal Log — all items by CSI section",
      "SWPPP / Erosion & Sediment Control Plan",
      "Construction Waste Management Plan",
      "Temporary Utilities and Facilities Plan",
      "Special Inspections Program coordination log",
      "Closeout documentation package: O&M manuals, as-builts, warranties",
    ],
    testingInspection: [
      "Special Inspections per project SIP as required by the building department",
      "Third-party air quality / SWPPP compliance monitoring if required by local authority",
      "Pre-final and final punch-list inspection with Architect and Owner",
    ],
    laborCategories: [
      "Project Manager",
      "Superintendent",
      "Field Engineer / Project Engineer",
      "Safety Officer",
      "Laborer",
      "Foreman",
    ],
  },

  div2: {
    divisionId: "div2",
    divisionName: "Division 02 — Existing Conditions / Demolition",
    tradeName: "Demolition Contractor",
    scopeItems: [
      "Furnish all labor, equipment, and materials to perform selective interior demolition as indicated on demolition drawings, including removal of non-structural partitions, ceilings, flooring, and casework.",
      "Demolish and remove existing roofing system(s), including membrane, insulation, blocking, and associated flashings as indicated.",
      "Remove existing exterior cladding, windows, and doors as indicated, including associated framing, trim, and flashings.",
      "Demolish existing concrete flatwork, curbing, sidewalks, and paving as indicated, sawcut at clean lines at limits of demolition.",
      "Excavate and remove existing underground utilities, piping, tanks, and foundations as shown on demolition plans; cap all utilities at designated points.",
      "Perform hazardous materials abatement (asbestos-containing materials, lead paint) per applicable regulations and abatement contractor qualifications prior to structural demolition.",
      "Furnish all required shoring, bracing, and temporary support for existing structure and adjacent construction during demolition operations.",
      "Protect all surfaces, finishes, and building systems to remain from damage during demolition; provide temporary weatherproofing at all new openings.",
      "Remove existing mechanical, electrical, and plumbing systems as indicated; coordinate with MEP trades for proper disconnection and capping sequences.",
      "Transport and legally dispose of all demolition debris at approved landfill or recycling facility; provide manifests and disposal receipts.",
      "Perform final cleanup of demolition area, broom-clean surfaces, and prepare substrate for subsequent work by following trades.",
      "Videotape and photograph existing conditions before and during demolition of all areas; provide copies to Contractor.",
      "Provide dust control, including temporary partitions, negative air pressure, and HEPA filtration units as required to protect occupied areas.",
    ],
    exclusions: [
      "New construction, patching, or infill work at demolished areas (by General Contractor or applicable trade unless noted).",
      "Hazardous materials testing and clearance sampling (by Owner-engaged industrial hygienist).",
      "Structural strengthening or underpinning beyond temporary shoring required for demolition sequence.",
      "Soil contamination remediation (by Owner's environmental contractor).",
      "Tree and vegetation removal beyond the limits of work area (by landscape contractor).",
      "Underground utility locating beyond standard 811 call (by Owner).",
    ],
    referenceSpecs: [
      "OSHA 29 CFR 1926 Subpart T — Demolition",
      "NFPA 241 — Safeguarding Construction, Alteration, and Demolition Operations",
      "EPA 40 CFR Part 61 Subpart M — National Emission Standard for Asbestos",
      "ASTM E1368 — Standard Practice for Visual Inspection of Asbestos-Abatement Projects",
      "ANSI A10.6 — Safety Requirements for Demolition Operations",
      "29 CFR 1926.1101 — OSHA Asbestos Standard for Construction",
      "HUD Guidelines for the Evaluation and Control of Lead-Based Paint Hazards in Housing",
    ],
    submittals: [
      "Selective Demolition Plan and sequence drawings",
      "Hazardous Materials Abatement Plan (asbestos/lead)",
      "Abatement contractor qualifications and certifications",
      "Shoring and bracing design by licensed PE",
      "Disposal manifests and facility receipts",
      "Pre-demolition photographs and video documentation",
      "Clearance testing reports (post-abatement)",
    ],
    testingInspection: [
      "Pre-abatement air sampling and bulk sampling (Owner-furnished industrial hygienist)",
      "Post-abatement clearance air monitoring per AHERA/OSHA",
      "Lead clearance wipe sampling where required",
      "Post-demolition survey to verify scope completion before next trade mobilizes",
    ],
    laborCategories: [
      "Demolition Foreman",
      "Demolition Laborer",
      "Equipment Operator",
      "Abatement Supervisor",
      "Abatement Worker",
      "Truck Driver",
    ],
  },

  div3: {
    divisionId: "div3",
    divisionName: "Division 03 — Concrete",
    tradeName: "Concrete Contractor",
    scopeItems: [
      "Furnish and install all concrete formwork and shoring for footings, grade beams, foundation walls, columns, beams, slabs, and elevated decks as shown on structural drawings.",
      "Furnish and install all reinforcing steel (rebar) per structural drawings, including chairs, ties, splices, and all required clearances; fabricate from ASTM A615 Grade 60 or A706 as specified.",
      "Furnish and place all cast-in-place concrete for foundations, grade beams, and perimeter walls per mix designs approved by structural engineer of record.",
      "Furnish and place all cast-in-place concrete for slab-on-grade, including all required sub-base preparation, vapor retarder, edge forms, and control joints.",
      "Furnish and place all cast-in-place concrete for elevated slabs, including composite metal deck support, shear studs, and edge conditions.",
      "Install all cast-in embeds, anchor bolts, blockouts, sleeves, and insert plates as shown on structural and MEP drawings; coordinate with all affected trades prior to each pour.",
      "Furnish and install post-tensioning system at elevated slabs as indicated; system to be designed and installed by a specialty post-tensioning subcontractor under PE stamp.",
      "Furnish and apply concrete curing compounds, sealers, and curing blankets as required for weather protection; comply with ACI 308 for all curing procedures.",
      "Furnish and install non-shrink grout at all column base plates, equipment pads, and mechanical/electrical equipment anchor locations.",
      "Furnish and install concrete for all equipment pads, curbs, housekeeping pads, and architectural concrete elements as shown.",
      "Perform all required concrete testing including slump, air content, temperature, and cylinder fabrication; engage Owner-designated testing laboratory.",
      "Furnish and install all waterstops, construction joint materials, and expansion joint materials as shown.",
      "Provide all final concrete flatwork finishing including broom finish, trowel finish, exposed aggregate, and specified surface treatments.",
      "Saw-cut all control joints at slab-on-grade within 24 hours of placement in pattern as indicated or per ACI 302 recommendations.",
    ],
    exclusions: [
      "Structural engineering of record and geotechnical report (provided by Owner).",
      "Pre-cast concrete elements (separate sub-contractor).",
      "Architectural exposed concrete finishing beyond standard form tie patching and bug hole grouting.",
      "Underslab waterproofing membrane and drainage board (by waterproofing contractor).",
      "Concrete testing laboratory services (Owner-furnished).",
      "Concrete staining, grinding, polishing, or epoxy coating (by flooring contractor).",
      "Fill or infill concrete at existing openings, demolition voids, or below existing elevated slabs.",
    ],
    referenceSpecs: [
      "ACI 318 — Building Code Requirements for Structural Concrete",
      "ACI 301 — Specifications for Structural Concrete",
      "ACI 302.1R — Guide to Concrete Floor and Slab Construction",
      "ACI 308R — Guide to External Curing of Concrete",
      "ASTM C94 — Standard Specification for Ready-Mixed Concrete",
      "ASTM A615 / A706 — Deformed and Plain Steel Bars for Concrete Reinforcement",
      "ASTM C150 — Portland Cement",
      "CRSI — Manual of Standard Practice",
    ],
    submittals: [
      "Concrete mix designs for each class of concrete (foundations, SOG, structural)",
      "Reinforcing steel shop drawings and bar lists (PE-stamped)",
      "Rebar mill certifications (ASTM A615/A706)",
      "Post-tensioning shop drawings and calculations (PE-stamped)",
      "Formwork shoring design by licensed PE",
      "Concrete placement and curing plan",
      "Hot/cold weather concrete procedures",
      "Non-shrink grout product data",
      "Vapor retarder product data",
      "Waterstop and joint filler product data",
    ],
    testingInspection: [
      "Concrete cylinder breaks (7-day and 28-day) per ACI 301 — Owner's testing lab",
      "Slump, air content, temperature, and unit weight testing at point of discharge",
      "Reinforcing steel placement inspection by special inspector",
      "Post-tension stressing records by PT specialty contractor",
      "Flatness/levelness survey (FF/FL) at slabs per ACI 117",
      "Pre-pour inspection of formwork and embeds by structural engineer",
    ],
    laborCategories: [
      "Concrete Foreman",
      "Cement Mason / Finisher",
      "Ironworker (rebar)",
      "Concrete Laborer",
      "Equipment Operator",
      "Form Carpenter",
    ],
  },

  div4: {
    divisionId: "div4",
    divisionName: "Division 04 — Masonry",
    tradeName: "Masonry Contractor",
    scopeItems: [
      "Furnish and install all concrete masonry unit (CMU) walls, including standard, lightweight, and architectural CMU as indicated, with specified bond pattern, coursing, and mortar joints.",
      "Furnish and install all horizontal joint reinforcement, vertical rebar, and grouting of CMU cells per structural drawings and specifications.",
      "Furnish and install all masonry lintels, including CMU bond beam lintels and precast/steel lintels at openings as shown.",
      "Furnish and install all brick veneer and face brick at exterior and interior locations per architectural drawings, including proper ties, weeps, and cavity conditions.",
      "Furnish and install all natural stone and manufactured stone veneer at exterior and interior locations as indicated, including setting bed, mortar, and grout.",
      "Furnish and install all masonry control joints, expansion joints, and movement joints at locations per structural engineer of record.",
      "Furnish and install all through-wall flashings, cavity drainage, and weep systems at base-of-wall and above all openings in masonry cavity walls.",
      "Furnish and install all masonry reinforcement accessories including wire ties, anchor ties, shelf angles, and embedments as shown.",
      "Construct masonry mock-up panel(s) as required by specifications; obtain Architect approval prior to proceeding with production work.",
      "Furnish and install all required masonry cleaning and sealing upon completion, using approved products compatible with unit type.",
      "Provide all required protection of masonry work from weather during construction, including cold-weather enclosures and hot-weather sunshades per ACI 530.1.",
      "Furnish and install cast stone, architectural precast, or GFRC details at accent locations as indicated.",
    ],
    exclusions: [
      "Structural steel lintels and shelf angles (provided by metals contractor unless noted).",
      "Spray fireproofing on structural steel embedded within masonry.",
      "Waterproofing, dampproofing, and parge coatings at below-grade masonry (by waterproofing contractor).",
      "Interior plaster and stucco finishes over masonry (by finishes contractor).",
      "Masonry restoration or repointing at existing historic masonry (separate allowance required).",
      "Air barrier system at masonry cavity (by thermal/moisture contractor).",
      "Structural engineering of record for masonry design.",
    ],
    referenceSpecs: [
      "ACI 530 / ASCE 5 / TMS 402 — Building Code Requirements and Specification for Masonry Structures",
      "ACI 530.1 / TMS 602 — Specification for Masonry Structures",
      "ASTM C90 — Loadbearing Concrete Masonry Units",
      "ASTM C216 — Facing Brick (Solid Masonry Units Made from Clay or Shale)",
      "ASTM C270 — Mortar for Unit Masonry",
      "ASTM C476 — Grout for Masonry",
      "BIA Technical Note Series — Brick Industry Association",
      "NCMA TEK Series — National Concrete Masonry Association",
    ],
    submittals: [
      "Masonry unit samples: brick, CMU, stone (color, texture, size)",
      "Mortar mix design and color sample",
      "Masonry wall shop drawings (bond pattern, coursing, control joints, details)",
      "Masonry reinforcement shop drawings",
      "Lintel and shelf angle shop drawings",
      "Mason contractor qualifications / ACI/TMS certification",
      "Mock-up approval documentation",
      "Flashing and weep system product data",
    ],
    testingInspection: [
      "Masonry prism tests (ASTM E447) per project specification requirements",
      "Mortar cube compression tests",
      "Special inspection of masonry construction per IBC Chapter 17",
      "Grout pour inspection by special inspector",
      "Visual inspection of wall ties, flashing, and weep placement",
    ],
    laborCategories: [
      "Mason Foreman",
      "Journeyman Mason (brick/block)",
      "Mason Tender / Hod Carrier",
      "Stone Setter",
      "Laborer",
    ],
  },

  div5: {
    divisionId: "div5",
    divisionName: "Division 05 — Metals",
    tradeName: "Structural Steel / Metals Contractor",
    scopeItems: [
      "Furnish and erect all structural steel framing including wide-flange columns, beams, girders, braced frames, moment frames, and kickers as shown on structural drawings.",
      "Furnish and install all steel joists and joist girders (SJI standard series) as shown, including bridging, bridging anchors, and end bearings.",
      "Furnish and install all steel roof deck and composite floor deck including shear studs on composite beams; deck to be factory-primed; field-weld and screw attachment per SDI standards.",
      "Furnish and install all miscellaneous metal fabrications including base plates, bearing plates, gusset plates, clip angles, stiffeners, and connection hardware.",
      "Furnish and install all metal stairs, including stringers, treads, risers, landings, framing, and connections per structural drawings and applicable code.",
      "Furnish and install all handrails, guardrails, and intermediate rails at all stairs, ramps, mezzanines, and elevated platforms per IBC and project details.",
      "Furnish and install all structural steel erection bracing and temporary connections as required for stability during erection.",
      "Furnish and install all steel ladders, ship's ladders, catwalks, platforms, and safety cages as shown.",
      "Furnish and install all miscellaneous steel lintels, ledger angles, embed plates, anchor bolts, and preset anchors as shown on drawings.",
      "Furnish and apply all shop-applied primer coat to all structural steel and miscellaneous metals not to receive spray fireproofing or galvanizing.",
      "Furnish and install all hot-dipped galvanized metals at exterior locations as indicated, including pipe rails, lintels, and angle frames.",
      "Furnish and install all ornamental and decorative metal railings at interior and exterior locations per architectural details.",
      "Perform all field welding and bolted connections per AWS D1.1 and connection design drawings; provide required inspection access to special inspector.",
      "Provide structural engineer of record review of steel shop drawings and issue approval prior to fabrication.",
    ],
    exclusions: [
      "Spray-applied intumescent or cementitious fireproofing (by fireproofing contractor).",
      "Cold-formed steel framing / light gauge framing (by framing contractor).",
      "Structural engineering of record (Owner-furnished).",
      "Concrete foundation work including anchor bolt setting (by concrete contractor).",
      "Architectural metal ceilings, panels, and specialty metal finishes (by finishes contractor).",
      "Aluminum storefront, curtain wall, and window framing (by glazing contractor).",
      "Elevator shaft rails and equipment (by elevator contractor).",
      "Cathodic protection systems for buried metals.",
    ],
    referenceSpecs: [
      "AISC 360 — Specification for Structural Steel Buildings",
      "AISC 303 — Code of Standard Practice for Steel Buildings and Bridges",
      "AISC 341 — Seismic Provisions for Structural Steel Buildings",
      "AWS D1.1 — Structural Welding Code — Steel",
      "ASTM A36, A992, A500 — Steel Material Certifications",
      "SJI CJ-Series / K-Series — Steel Joist Specifications",
      "SDI — Steel Deck Institute Design Manual",
      "OSHA 29 CFR 1926 Subpart R — Steel Erection",
    ],
    submittals: [
      "Structural steel shop drawings and calculations (PE-stamped)",
      "Steel mill certifications (ASTM A36/A992/A500/A572)",
      "High-strength bolt certifications (ASTM A325/A490/F3125)",
      "Welding Procedure Specifications (WPS) and welder certifications (CWI-verified)",
      "Steel joist shop drawings and approval",
      "Steel deck shop drawings",
      "Miscellaneous metals fabrication drawings",
      "Handrail and guardrail shop drawings and finish samples",
      "Stair shop drawings",
      "Steel erection plan and sequence",
      "Primer/coating product data",
    ],
    testingInspection: [
      "High-strength bolt installation inspection — pre-installation verification and in-process inspection by special inspector",
      "Welding inspection (CWI) per AWS D1.1 — complete and partial joint penetration welds",
      "Ultrasonic testing (UT) or radiographic testing (RT) of CJP welds per structural EOR",
      "Steel deck attachment inspection by special inspector",
      "Anchor bolt setting survey prior to column erection",
      "AISC quality certification verification for fabricator and erector",
    ],
    laborCategories: [
      "Ironworker Connector",
      "Ironworker Welder",
      "Ironworker Reinforcing (if applicable)",
      "Equipment Operator (crane/rigging)",
      "Ironworker Foreman",
      "Laborer",
    ],
  },

  div6: {
    divisionId: "div6",
    divisionName: "Division 06 — Wood, Plastics & Composites",
    tradeName: "Carpentry / Millwork Contractor",
    scopeItems: [
      "Furnish and install all rough carpentry framing including exterior walls, interior partitions, floor framing, and roof framing as shown on structural and architectural drawings.",
      "Furnish and install all engineered lumber including LVL, LSL, PSL, and glulam beams and headers as shown; coordinate with structural engineer of record for connection design.",
      "Furnish and install all wood I-joist floor and roof framing per engineered drawings; provide all required blocking, rim boards, and bearing conditions per manufacturer requirements.",
      "Furnish and install all structural sheathing (plywood or OSB) at walls, floors, and roofs; nail/screw per shear wall schedule and diaphragm diagrams.",
      "Furnish and install all pressure-treated lumber at all sill plates, ledgers, blocking in contact with concrete or masonry, and within 18 inches of grade.",
      "Furnish and install all fire-retardant-treated (FRT) lumber and plywood in locations required by code, including roof framing in Type III and Type V-A construction.",
      "Furnish and install all exterior trim, fascia, rake, corner boards, window and door surrounds, soffits, and eave details in materials as specified.",
      "Furnish and install all interior finish carpentry including window and door casing, base molding, crown molding, chair rail, wainscoting, and interior trim package.",
      "Furnish and install all custom architectural millwork, cabinetry, and casework per architectural millwork drawings; work to meet AWI Premium Grade or Custom Grade as specified.",
      "Furnish and install all interior built-in furniture including bookcases, banquettes, mudroom lockers, closet systems, and display shelving as shown.",
      "Furnish and install all countertops at millwork and casework locations; material and edge profile per schedule.",
      "Furnish and install all blocking and backing for future owner-installed items including TV mounts, art hardware, grab bars, and handrail brackets.",
      "Furnish and install all specialty wood features including coffered ceilings, decorative beams, paneled walls, and feature walls as detailed.",
      "Install all Owner-furnished hardware at cabinetry and millwork including hinges, drawer slides, catches, and pulls; verify hardware submittals prior to installation.",
    ],
    exclusions: [
      "Structural engineering of record for framing design (Owner-furnished).",
      "Pre-manufactured roof truss fabrication engineering (truss manufacturer-furnished).",
      "Finish hardware at doors (by hardware contractor).",
      "Flooring installation (by flooring contractor).",
      "Painting and finishing of millwork in field (by painting contractor, unless millwork is factory-finished).",
      "Plumbing rough-in backing and blocking (coordinate with plumbing contractor).",
      "Prefabricated cabinets from Owner-specified vendors where direct-purchase applies.",
    ],
    referenceSpecs: [
      "AWI / AWMAC Architectural Woodwork Standards",
      "APA — Engineered Wood Construction Guide",
      "WI Quality Standards — Woodwork Institute",
      "ASTM D2898 — Performance of Pressure-Treated Wood for Structural Use",
      "ICC-ES AC04 — Fire-Retardant-Treated Wood",
      "NDS — National Design Specification for Wood Construction",
      "NFPA 703 — Standard for Fire Retardant–Treated Wood and Fire-Retardant Coatings for Building Materials",
    ],
    submittals: [
      "Lumber species, grade, and moisture content certification",
      "Pressure treatment and FRT wood treatment certifications",
      "Engineered lumber (LVL/PSL/Glulam) shop drawings and engineering",
      "Wood I-joist layout and shop drawings",
      "Architectural woodwork shop drawings (millwork/casework) — AWI grade certification",
      "Countertop material samples and edge profile samples",
      "Wood finish samples (stain color, species, sheen level)",
      "Hardware schedule for millwork",
    ],
    testingInspection: [
      "Moisture content testing of lumber before installation (must be 19% or less per NDS)",
      "Shear wall nailing inspection by special inspector where required by building department",
      "AWI grade compliance verification by certified inspector (if AWI Premium specified)",
      "Mock-up approval for stain/finish color before production run",
    ],
    laborCategories: [
      "Carpenter Foreman",
      "Journeyman Carpenter (rough framing)",
      "Finish Carpenter",
      "Cabinet Installer",
      "Millwork Installer",
      "Laborer",
    ],
  },

  div7: {
    divisionId: "div7",
    divisionName: "Division 07 — Thermal & Moisture Protection",
    tradeName: "Roofing Contractor",
    scopeItems: [
      "Furnish and install new slate roofing system including natural slate tiles at specified exposure, head lap, and pattern; all slates to be from approved quarry with uniform color and thickness.",
      "Furnish and install EPDM single-ply membrane roofing at low-slope areas per NRCA EPDM Roofing Manual; 60-mil fully-adhered system with manufacturer-certified installer.",
      "Furnish and install all sheet metal flashings including base flashings, counter-flashings, cap flashings, step flashings, chimney flashings, and valley flashings in 24-gauge stainless steel or lead-coated copper as specified.",
      "Furnish and install gutters and downspouts in profiles and materials as shown; connect to underground drainage system provided by site contractor.",
      "Furnish and install ice and water shield self-adhering membrane at all eaves (minimum 24 inches past interior face of exterior wall), valleys, rakes, and all penetrations.",
      "Furnish and install roofing underlayment (ASTM D6877 or equivalent) beneath slate and at all specified locations; lap and fasten per manufacturer requirements.",
      "Coordinate and prepare all penetration curbs and sleeves; receive and flash all HVAC, plumbing, and electrical roof penetrations from other trades; all penetrations to be reviewed and accepted before flashing commences.",
      "Furnish and install snow guards at all eave conditions per engineer of record design; spacing and quantity to prevent uncontrolled snow shedding at occupied areas and building entry points.",
      "Construct masonry/roofing mock-up panel at location directed by Architect; obtain Architect written approval of slate, flashing, and detail quality before proceeding.",
      "Furnish and install all roof insulation (polyisocyanurate or XPS) at specified R-value; taper board system at low-slope areas to achieve minimum 1/4-inch-per-foot positive drainage.",
      "Furnish and install vapor retarder at specified locations, fully adhered and lapped per ASHRAE 160 requirements.",
      "Furnish and install all air barrier system at exterior wall sheathing; all laps, terminations, and transitions to be taped per manufacturer requirements.",
      "Perform all required core cutting and infill patching at existing penetrations being eliminated; verify deck condition before recovering.",
      "Furnish all required hoisting, rigging, and staging for roofing operations; debris chutes at all story drops.",
      "Provide 2-year workmanship warranty and submit for applicable manufacturer's NDL (No Dollar Limit) roof system warranty upon project completion.",
    ],
    exclusions: [
      "Structural roof decking (by metals or framing contractor).",
      "Skylights and roof hatches — receive and flash only; supply and structural support by others.",
      "Roof drainage piping below deck (by plumbing contractor).",
      "Roof access hatches, mechanical equipment platforms, and screen walls (by general framing contractor).",
      "Roof-mounted mechanical and electrical equipment, including condenser pads and equipment screens.",
      "Lightning protection systems (by specialty lightning protection contractor).",
      "Photovoltaic solar panels and mounting systems (by electrical contractor or Owner-direct).",
      "Green roof / vegetated roof planting layer (by landscape contractor); waterproofing component included if in scope.",
    ],
    referenceSpecs: [
      "NRCA Roofing Manual — Membrane Roof Systems (current edition)",
      "NRCA Roofing Manual — Metal Panel and SPF Roof Systems",
      "National Slate Association — Slate Roofs: A Design and Installation Guide",
      "ASTM D4637 — Standard Specification for EPDM Sheet Used in Single-Ply Roof Membrane",
      "ASTM D6878 — Standard Specification for Thermoplastic Polyolefin Based Sheet Roofing (TPO)",
      "ASTM C578 — Standard Specification for Rigid, Cellular Polystyrene Thermal Insulation",
      "ASTM D1970 — Standard Specification for Self-Adhering Polymer Modified Bituminous Sheet Materials",
      "SMACNA — Architectural Sheet Metal Manual (7th Edition)",
    ],
    submittals: [
      "Slate sample panel — quarry identification, color range, thickness",
      "EPDM membrane product data and installation details",
      "Sheet metal flashing shop drawings and material certification",
      "Gutter and downspout shop drawings and profiles",
      "Ice and water shield product data (ASTM D1970)",
      "Underlayment product data",
      "Roof insulation product data (R-value, tapered board layout)",
      "Snow guard shop drawings and engineer of record review",
      "Manufacturer's installer certification",
      "Mock-up approval documentation",
      "Pre-roofing deck condition survey and photographs",
    ],
    testingInspection: [
      "Pre-roofing deck inspection — substrate condition, moisture content, fastener pullout if required",
      "Infrared thermographic moisture scan of completed membrane (Owner-furnished)",
      "EPDM seam probe testing and holiday detection at all field seams",
      "Sheet metal soldered joint flood test at critical locations",
      "Manufacturer's final inspection for NDL warranty issuance",
      "Drainage test at all scuppers, drains, and downspout connections",
    ],
    laborCategories: [
      "Roofing Foreman",
      "Journeyman Roofer (slate/membrane)",
      "Sheet Metal Worker",
      "Roofer Helper",
      "Laborer",
    ],
  },

  div8: {
    divisionId: "div8",
    divisionName: "Division 08 — Openings",
    tradeName: "Glazing / Door & Hardware Contractor",
    scopeItems: [
      "Furnish and install all exterior aluminum curtain wall systems per architectural drawings; thermally broken frames, specified glass type, air and water tested assemblies per AAMA/WDMA.",
      "Furnish and install all storefront framing systems at entrance bays and retail fronts; aluminum thermally broken system with specified finish and glass.",
      "Furnish and install all exterior windows per window schedule; aluminum, fiberglass, or wood-clad units as specified; all units NFRC rated.",
      "Furnish and install all exterior doors and frames including hollow metal, aluminum, and wood units per door schedule; include all required hardware prep.",
      "Furnish and install all interior hollow metal frames and steel doors at rated and non-rated assemblies per door schedule.",
      "Furnish and install all interior wood doors (solid core, hollow core, fire-rated) and wood frames per door schedule.",
      "Furnish and install all finish door hardware including locksets, closers, hinges, kick plates, stops, thresholds, and weather-stripping per hardware schedule.",
      "Furnish and install all overhead coiling doors, sectional garage doors, and fire-rated rolling shutters per opening schedule.",
      "Furnish and install all automatic door openers, sliding door operators, and access control rough-in coordination as indicated.",
      "Furnish and install all interior glazed partitions, glass doors, and frameless glass assemblies as shown.",
      "Furnish and install all interior borrowed-lite frames, sidelites, and transoms as indicated on drawings.",
      "Furnish and install all fire-rated glazing assemblies at rated corridor walls and partitions per fire door assembly listings.",
      "Furnish and install all glass in doors and sidelites including safety glazing compliant with IBC Section 2406 at all hazardous locations.",
      "Provide door hardware keying schedule and coordinate master keying with Owner's representative; include all key blanks per keying plan.",
    ],
    exclusions: [
      "Rough opening framing (by framing contractor).",
      "Perimeter sealant and caulking at window and door frames (by general caulking trade unless included in glazing scope).",
      "Electronic access control, card readers, and security hardware wiring (by electrical/security contractor).",
      "Overhead door motors, controls, and electrical rough-in (by electrical contractor; furnish and install by this contractor).",
      "Structural glass floors, glass railings (by specialty glazing contractor unless included herein).",
      "HVAC louvers and mechanical openings (by mechanical contractor).",
    ],
    referenceSpecs: [
      "AAMA 501 — Methods of Test for Exterior Windows, Curtain Walls, and Doors",
      "AAMA 2605 — Voluntary Specification for High Performance Organic Coatings on Aluminum",
      "ANSI/BHMA A156 Series — Builders Hardware Manufacturers Association Standards",
      "NFPA 80 — Standard for Fire Doors and Other Opening Protectives",
      "ASTM E283 — Air Leakage Testing of Windows and Doors",
      "ASTM E330 — Structural Performance of Exterior Windows, Doors, Skylights, and Curtain Walls",
      "ASTM E331 — Water Penetration Testing",
      "IBC Chapter 24 — Glass and Glazing / Chapter 7 — Fire-Resistance-Rated Construction",
    ],
    submittals: [
      "Curtain wall and storefront shop drawings",
      "Window schedule and product data",
      "Door and frame schedule — fire-rated assembly listings (UL/FM)",
      "Hardware schedule and keying plan",
      "Glass specifications and NFRC ratings",
      "Fire-rated door and glazing assembly listings",
      "Overhead/coiling door shop drawings",
      "Air and water test reports from independent laboratory (AAMA 501)",
    ],
    testingInspection: [
      "Field air and water infiltration testing of curtain wall and storefront per AAMA 503",
      "Fire door inspection and labeling verification by qualified inspector (NFPA 80)",
      "Hardware operation check and adjustment upon substantial completion",
      "Automatic door operator force and timing tests per ANSI A156.19",
    ],
    laborCategories: [
      "Glazier Foreman",
      "Journeyman Glazier",
      "Door and Hardware Installer",
      "Sheet Metal Worker (frames)",
      "Laborer",
    ],
  },

  div9: {
    divisionId: "div9",
    divisionName: "Division 09 — Finishes",
    tradeName: "Finishes Contractor",
    scopeItems: [
      "Furnish and install all metal stud framing and gypsum wallboard (GWB) assemblies at interior partitions and shaft walls; assemblies to comply with UL fire-resistance ratings as scheduled.",
      "Furnish and install all gypsum board ceiling and soffit systems; metal furring channels, resilient clips, and GWB as required by drawings and finish schedule.",
      "Furnish and install all exterior gypsum sheathing at steel stud exterior wall assemblies per ASTM C1278.",
      "Furnish and install all joint treatment (tape and three-coat finish) and Level 5 skim coat finish at all GWB surfaces receiving paint, per GA-214 Level 5 requirements.",
      "Furnish and install all ceramic, porcelain, and stone tile at all wall and floor locations per tile schedule; includes setting bed, Schluter strips, and perimeter sealant joints.",
      "Furnish and install all resilient flooring including sheet vinyl, LVT/LVP, rubber base, and transition strips per flooring schedule.",
      "Furnish and install all carpet and carpet tile at locations per flooring schedule; includes all adhesive, seam tape, stretch installation, and transitions.",
      "Furnish and install all hardwood flooring at locations per finish schedule; sand, fill, and finish in field unless pre-finished.",
      "Furnish and install all acoustical ceiling tile (ACT) and suspension grid systems per reflected ceiling plans; 2x2 and 2x4 grid types as specified.",
      "Furnish and install all specialty ceilings including linear metal, wood plank, acoustic panels, and clouds as shown on architectural drawings.",
      "Furnish and apply all interior painting including walls, ceilings, doors, frames, and miscellaneous metals; minimum two finish coats over one prime coat.",
      "Furnish and apply all exterior painting including wood trim, siding, doors, and surfaces per exterior finish schedule; products to be exterior-grade with specified warranty.",
      "Furnish and install all wall coverings, wallpaper, and fabric wall panels per interior design specifications and schedule.",
      "Furnish and install all corner guards, wall protection, impact-resistant panels, handrail covers, and specialty wall finishes in commercial/institutional areas.",
    ],
    exclusions: [
      "Concrete floor grinding, staining, sealing, or polishing (by specialty flooring contractor).",
      "Epoxy flooring systems in mechanical, lab, and industrial areas (by epoxy flooring contractor).",
      "Structural framing at walls and roofs (by framing contractor).",
      "Kitchen exhaust hood and commercial cooking equipment installation.",
      "Specialty decorative plaster and Venetian plaster beyond quoted allowance.",
      "Exterior stucco and EIFS systems (by thermal/moisture contractor).",
      "Window treatments, blinds, and draperies (by Owner or separate contract).",
    ],
    referenceSpecs: [
      "GA-216 — Application and Finishing of Gypsum Board",
      "GA-600 — Fire Resistance Design Manual",
      "ANSI A108 / A118 — American National Standard Specifications for Tile Installation",
      "TCNA Handbook for Ceramic, Glass, and Stone Tile Installation",
      "ASTM F1700 — Specification for Resilient Floor Coverings",
      "NWFA/NOFMA — Hardwood Floor Installation Guidelines",
      "PDCA / MPI — Master Painters Institute Painting Standards",
      "ASTM E84 — Surface Burning Characteristics of Building Materials (flame spread / smoke development)",
    ],
    submittals: [
      "GWB partition assembly drawings and UL fire-rating certifications",
      "Tile samples (color, texture, grout selection) and setting system product data",
      "Flooring samples and product data (carpet, LVT, rubber, hardwood)",
      "Paint color samples, product data, and MPI category confirmation",
      "Acoustical ceiling tile samples and suspension system product data",
      "Wall covering samples and VOC documentation",
      "Level 5 finish specification reference (GA-214)",
    ],
    testingInspection: [
      "Moisture testing of concrete substrate before resilient flooring installation (ASTM F2170 RH probe or ASTM F1869 calcium chloride)",
      "Tile bond test per ANSI A108 after curing — minimum 80% coverage (95% at wet areas)",
      "Paint adhesion test at each surface type after first coat",
      "Flatness and levelness survey of GWB assemblies before tile and resilient flooring installation",
    ],
    laborCategories: [
      "Drywall / Framing Foreman",
      "Journeyman Drywall Finisher (taper)",
      "Tile Setter",
      "Flooring Installer",
      "Painter Foreman",
      "Journeyman Painter",
      "Laborer",
    ],
  },

  div22: {
    divisionId: "div22",
    divisionName: "Division 22 — Plumbing",
    tradeName: "Plumbing Contractor",
    scopeItems: [
      "Furnish and install all domestic cold water distribution piping from the building water service entry to all fixtures and equipment; type L copper or approved alternative.",
      "Furnish and install all domestic hot water distribution piping including supply and recirculation systems; insulate all hot water piping per energy code.",
      "Furnish and install domestic hot water heaters, boilers, and storage tanks as scheduled; include all gas, venting, condensate, and relief valve piping.",
      "Furnish and install all sanitary drain, waste, and vent (DWV) piping from all fixtures to building sanitary sewer service; cast iron hub and spigot below slab, PVC above.",
      "Furnish and install all storm drainage piping including roof drain connections, interior leaders, and storm sewer connection to site utility by civil contractor.",
      "Furnish and install all plumbing fixtures and fittings per plumbing fixture schedule including water closets, lavatories, sinks, showers, bathtubs, and floor drains.",
      "Furnish and install all floor drains, roof drains, area drains, hub drains, and trench drains per plumbing drawings.",
      "Furnish and install all interceptors including grease interceptors, sand/oil interceptors, and medical/chemical waste neutralization units as required.",
      "Furnish and install all natural gas piping from meter to all equipment connections; include pressure testing, bonding, and seismic shutoff valves per local code.",
      "Furnish and install all plumbing rough-in for Owner-furnished equipment including dishwashers, ice makers, drinking fountains, and specialty lab equipment.",
      "Furnish and install all backflow preventers, pressure reducing valves, expansion tanks, and other appurtenances as required by code and drawings.",
      "Perform all required pressure and leakage testing and balancing; provide signed inspection certificates and test reports.",
      "Coordinate plumbing sleeve penetrations through all fire-rated assemblies; provide and install fire-rated pipe penetration seals.",
      "Provide all required trenching and backfill for below-slab plumbing; compact backfill to 95% per geotechnical report recommendations.",
    ],
    exclusions: [
      "Site utilities beyond building 5-foot line (by site/civil contractor).",
      "Structural modifications for pipe chases and shaft framing (by structural contractor).",
      "Fire suppression / sprinkler piping (by fire protection contractor).",
      "Medical gas systems (by medical gas specialty contractor).",
      "Process piping beyond standard utility connections.",
      "Water softener systems (by mechanical contractor unless noted).",
      "Pool and spa plumbing systems (by pool contractor).",
    ],
    referenceSpecs: [
      "IPC — International Plumbing Code (current adopted edition)",
      "ASME B31.9 — Building Services Piping",
      "ASTM B88 — Seamless Copper Water Tube (Types K, L, M)",
      "ASTM D1785 — Poly(Vinyl Chloride) (PVC) Plastic Pipe",
      "CISPI 301 — Standard Specification for Hubless Cast Iron Soil Pipe and Fittings",
      "ASSE 1010 — Hydraulic Shock Arresters (Water Hammer Arrestors)",
      "ASSE 1020 — Pressure Reducing Valves for Domestic Water Supply Systems",
      "NSF/ANSI 61 — Drinking Water System Components — Health Effects",
    ],
    submittals: [
      "Plumbing fixture schedule and product data",
      "Pipe and fitting material certifications",
      "Water heater/boiler product data and efficiency ratings",
      "Backflow preventer product data and ASSE certification",
      "Grease interceptor product data and sizing calculations",
      "Fire-rated pipe penetration seal product data",
      "Pressure test reports (domestic water, gas)",
    ],
    testingInspection: [
      "Rough plumbing inspection by building department before cover-up",
      "Domestic water system pressure test: 150 PSI for 4 hours minimum",
      "Gas piping pressure test per IFGC Section 406",
      "Final plumbing inspection with building official",
      "Backflow preventer field testing by certified tester (annual thereafter by Owner)",
    ],
    laborCategories: [
      "Master Plumber / Foreman",
      "Journeyman Plumber",
      "Plumber's Apprentice",
      "Pipe Welder / Brazer",
      "Laborer",
    ],
  },

  div23: {
    divisionId: "div23",
    divisionName: "Division 23 — HVAC (Mechanical)",
    tradeName: "Mechanical (HVAC) Contractor",
    scopeItems: [
      "Furnish and install all HVAC equipment per mechanical schedule including air handling units, fan coil units, rooftop units, heat pumps, chillers, cooling towers, and boilers.",
      "Furnish and install all ductwork systems (supply, return, exhaust, and outside air) in galvanized steel fabricated per SMACNA HVAC Duct Construction Standards; provide all access doors, balancing dampers, and turning vanes.",
      "Furnish and install all piping systems including chilled water, hot water, condenser water, refrigerant, steam, and condensate return in materials per mechanical specifications.",
      "Furnish and install all automatic temperature control (ATC) and building automation system (BAS) controls, including all sensors, actuators, VFDs, DDC controllers, and programming for all HVAC systems.",
      "Furnish and install all diffusers, grilles, registers, and specialty air distribution devices per reflected ceiling plans and mechanical schedules.",
      "Furnish and install all exhaust fans, inline fans, kitchen hood exhaust systems, and toilet exhaust systems as indicated.",
      "Furnish and install all refrigerant piping for split and VRF/VRV systems; leak test and commission per ASHRAE 15 and manufacturer requirements.",
      "Furnish and install all duct and pipe insulation in compliance with energy code (ASHRAE 90.1 or IECC) requirements.",
      "Perform complete air and hydronic system balancing by a NEBB-certified balancing contractor; provide TAB report.",
      "Furnish and install all vibration isolation hangers, seismic restraints, and flexible connections at all rotating equipment per SMACNA Seismic Restraint Manual.",
      "Furnish and install all combustion air, intake, and exhaust louvers; coordinate penetrations with roofing and envelope contractors.",
      "Commission all mechanical systems per project commissioning plan (Cx); provide all startup reports, controls sequences, and O&M documentation.",
      "Coordinate all mechanical room layout with plumbing, electrical, and fire protection; provide coordination drawings in CAD or BIM as required.",
      "Provide all required permits, pressure tests, and government inspections for HVAC and refrigeration systems.",
    ],
    exclusions: [
      "Electrical service to equipment panels and VFDs (by electrical contractor); coordinate load data.",
      "Fire dampers in duct penetrations of rated assemblies (by HVAC contractor) — firestopping seal by fire protection contractor.",
      "Plumbing connections to AHU drain pans and humidifiers (by plumbing contractor).",
      "Structural supports and roof curbs for rooftop equipment (by structural/roofing contractor unless noted).",
      "Owner-furnished kitchen equipment connections beyond stubout provided.",
      "Commissioning agent services (Owner-furnished, coordinate as required).",
    ],
    referenceSpecs: [
      "ASHRAE Standard 90.1 — Energy Standard for Buildings",
      "ASHRAE Standard 15 — Safety Standard for Refrigeration Systems",
      "SMACNA HVAC Duct Construction Standards — Metal and Flexible",
      "SMACNA HVAC Systems Testing, Adjusting & Balancing",
      "NEBB — Procedural Standards for Testing, Adjusting & Balancing",
      "ASME B31.5 — Refrigeration Piping and Heat Transfer Components",
      "IMC — International Mechanical Code (current adopted edition)",
      "NFPA 96 — Standard for Ventilation Control and Fire Protection of Commercial Cooking Operations",
    ],
    submittals: [
      "HVAC equipment schedules and product data (AHUs, RTUs, FCUs, chillers, boilers)",
      "Ductwork shop drawings and duct pressure class schedule",
      "Piping system shop drawings and materials",
      "BAS/Controls sequence of operations and point list",
      "Insulation product data and compliance with energy code",
      "TAB (Testing, Adjusting, Balancing) contractor qualifications and report format",
      "Seismic restraint calculations by licensed PE",
      "Refrigerant type and charge per ASHRAE 34 safety classification",
    ],
    testingInspection: [
      "Ductwork leakage testing per SMACNA standards (Class A leakage at ≥2-inch pressure class)",
      "Hydronic piping pressure tests: 1.5 × design pressure for 4 hours",
      "Refrigerant leak detection test per ASHRAE 15",
      "Mechanical rough-in and above-ceiling inspection by building department",
      "TAB final report verification — air and water systems within NEBB tolerances",
      "BAS controls functional testing and trending by commissioning agent",
    ],
    laborCategories: [
      "HVAC Foreman",
      "Journeyman Sheet Metal Worker",
      "Journeyman Pipefitter / Steamfitter",
      "Controls Technician",
      "Refrigeration Mechanic (EPA 608 certified)",
      "BAS Programmer",
      "Laborer",
    ],
  },

  div26: {
    divisionId: "div26",
    divisionName: "Division 26 — Electrical",
    tradeName: "Electrical Contractor",
    scopeItems: [
      "Furnish and install the electrical service entrance including service conduits, conductors, meter socket, and main switchboard or service entrance equipment; coordinate utility company requirements.",
      "Furnish and install all distribution equipment including main and branch distribution panels, switchboards, motor control centers (MCC), and transfer switches per one-line diagrams.",
      "Furnish and install all branch circuit wiring for power, lighting, HVAC equipment, plumbing equipment, and specialty systems; copper conductors in conduit throughout.",
      "Furnish and install all lighting fixtures per reflected ceiling plans and lighting schedule; includes LED drivers, dimmer controls, and daylight sensors.",
      "Furnish and install all lighting control systems including occupancy sensors, astronomical timeclocks, daylight harvesting controls, and central lighting control panels.",
      "Furnish and install all emergency and egress lighting systems, including unit equipment, exit signs, and central battery systems per NFPA 101.",
      "Furnish and install all low-voltage rough-in conduit and pull strings for data, telephone, AV, security, and fire alarm systems.",
      "Furnish and install all grounding and bonding systems per NEC Article 250, including ground ring, ground electrodes, lightning protection connections, and equipment grounds.",
      "Furnish and install all raceway systems including EMT, rigid conduit, MC cable (where permitted), cable trays, and surface raceways as indicated.",
      "Furnish and install all power connections to HVAC equipment, plumbing equipment, kitchen equipment, and Owner-furnished equipment per electrical drawings.",
      "Furnish and install all standby generator system including generator, transfer switch, fuel system, exhaust, and controls; load bank test per NFPA 110.",
      "Furnish and install all photovoltaic solar system including panels, inverters, combiner boxes, monitoring, and utility interconnection where indicated.",
      "Furnish and install all EV charging station rough-in, panels, and complete EVSE units as indicated.",
      "Provide all required permits, inspections, and coordination with utility company for service energization.",
      "Perform all electrical testing including infrared scanning of energized switchgear, megger testing of feeders, and ground fault testing.",
    ],
    exclusions: [
      "Utility company primary service extension, transformer, and meter (utility-furnished and installed; coordinate permit and fees).",
      "Fire alarm system (by life safety contractor).",
      "Structured cabling (data and voice) terminations and equipment (by technology contractor).",
      "Security, CCTV, and access control equipment terminations (by security contractor).",
      "Audio-visual systems beyond conduit rough-in (by AV contractor).",
      "Elevator electrical feeders (by this contractor); elevator controller and car wiring (by elevator contractor).",
      "Hazardous location (Class I, Division I) wiring (separate specialty contractor).",
    ],
    referenceSpecs: [
      "NFPA 70 — National Electrical Code (NEC), current adopted edition",
      "NFPA 70E — Standard for Electrical Safety in the Workplace",
      "NFPA 110 — Standard for Emergency and Standby Power Systems",
      "NFPA 101 — Life Safety Code (emergency lighting)",
      "IEEE 1100 — Recommended Practice for Powering and Grounding Electronic Equipment",
      "NEMA MG 1 — Motors and Generators",
      "UL 508A — Standard for Industrial Control Panels",
      "ASHRAE 90.1 — Energy Standard (lighting power density limits)",
    ],
    submittals: [
      "One-line electrical diagrams — panel schedules, load calculations",
      "Switchboard and distribution panel shop drawings",
      "Lighting fixture schedule and cut sheets (IES files where required)",
      "Generator product data, load bank test procedure, and fuel system drawings",
      "Lighting control system product data and programming sequences",
      "Grounding and bonding system drawings",
      "EV charging station product data and utility interconnect plan",
      "Electrical short circuit and coordination study (arc flash analysis) by licensed PE",
    ],
    testingInspection: [
      "Rough-in electrical inspection by building department (pre-cover)",
      "Service entrance grounding inspection",
      "Megohmmeter insulation resistance test of all feeders",
      "Infrared (IR) thermographic scan of all energized switchgear and panels",
      "Load bank test of emergency generator per NFPA 110 (2-hour full-load)",
      "Ground fault and arc fault circuit interrupter (GFCI/AFCI) testing per NEC",
      "Lighting control system commissioning and daylight sensor calibration",
    ],
    laborCategories: [
      "Electrical Foreman",
      "Journeyman Electrician",
      "Electrician's Apprentice",
      "Low-Voltage Technician",
      "Instrumentation / Controls Technician",
      "Laborer",
    ],
  },

  div31: {
    divisionId: "div31",
    divisionName: "Division 31 — Earthwork",
    tradeName: "Earthwork / Grading Contractor",
    scopeItems: [
      "Furnish all labor, equipment, and materials to perform all site clearing, grubbing, and stripping of topsoil to depths indicated; stockpile topsoil on site for reuse at landscape areas.",
      "Perform all mass excavation including building pads, utility corridors, underground parking, and site cut/fill as shown on grading plan; dispose of unsuitable material off site.",
      "Perform all excavation for building foundations, grade beams, footings, elevator pits, and utility structures; maintain excavation shoring per OSHA 29 CFR 1926 Subpart P.",
      "Furnish and compact all structural fill for building pad, utility backfill, and slab subgrade; compaction to minimum 95% of modified Proctor maximum dry density (ASTM D1557).",
      "Furnish and install granular sub-base material (crushed stone or recycled concrete) at building slab-on-grade and pavement areas to thicknesses shown on civil drawings.",
      "Perform all fine grading at building perimeter, paved areas, and landscape areas per grading plan; establish positive drainage away from building.",
      "Install all subdrainage systems including perforated drain pipe, filter fabric, and drainage aggregate at foundation walls, planting areas, and lawn areas.",
      "Furnish and install all erosion and sediment control BMPs per SWPPP including silt fence, inlet protection, stabilized construction entrance, and sediment basins.",
      "Perform all rough grading and final grading at all landscape and turf areas; restore to indicated finish grades per civil drawings.",
      "Import and place select engineered fill material when native soils are not suitable for structural purposes; obtain Geotechnical Engineer approval of all fill materials.",
      "Provide dewatering operations as required to maintain dry excavation for foundations and utilities; dispose of pumped groundwater in compliance with environmental regulations.",
      "Coordinate and protect all existing underground utilities; call 811 and verify all utility locations by hand-digging at least 24 hours before power equipment use.",
    ],
    exclusions: [
      "Geotechnical investigation and report (Owner-furnished).",
      "Environmental remediation of contaminated soils (by Owner's environmental contractor).",
      "Underground utility installation beyond the building foundation perimeter (by site utilities contractor unless this scope includes both).",
      "Concrete foundations (by concrete contractor); subgrade preparation included.",
      "Landscape planting, sod, and irrigation (by landscape contractor).",
      "Rock blasting (included only if separate rock excavation allowance is provided herein).",
      "Offsite improvements beyond property line unless specifically included.",
    ],
    referenceSpecs: [
      "ASTM D1557 — Standard Test Methods for Laboratory Compaction Characteristics of Soil (Modified Proctor)",
      "ASTM D4318 — Liquid Limit, Plastic Limit, and Plasticity Index of Soils",
      "ASTM D698 — Standard Test Methods for Laboratory Compaction Characteristics (Standard Proctor)",
      "OSHA 29 CFR 1926 Subpart P — Excavations",
      "AASHTO T-99 / T-180 — Moisture-Density Relations",
      "USEPA National Pollutant Discharge Elimination System (NPDES) Construction General Permit",
      "FHWA Soils and Foundations Reference Manual",
    ],
    submittals: [
      "Existing conditions survey and utility locate documentation",
      "Grading and earthwork plan identifying cut/fill volumes",
      "SWPPP — Stormwater Pollution Prevention Plan with BMP locations",
      "Structural fill material source and gradation test results",
      "Subgrade bearing capacity test results from geotechnical engineer",
      "Dewatering plan and discharge permits",
      "Import fill material qualification report",
    ],
    testingInspection: [
      "Compaction testing (nuclear density gauge or sand cone) — minimum one test per 5,000 SF per lift",
      "Subgrade bearing capacity verification by geotechnical engineer before slab/footing concrete",
      "Imported fill material gradation and Proctor testing",
      "Trench excavation inspection before backfill (shoring compliance, utility depth)",
      "Final grade survey by licensed surveyor before paving/landscaping",
    ],
    laborCategories: [
      "Earthwork Foreman / Grade Foreman",
      "Equipment Operator (dozer)",
      "Equipment Operator (excavator)",
      "Equipment Operator (motor grader)",
      "Truck Driver",
      "Laborer",
    ],
  },

  div32: {
    divisionId: "div32",
    divisionName: "Division 32 — Exterior Improvements",
    tradeName: "Site / Landscape Contractor",
    scopeItems: [
      "Furnish and install all concrete flatwork including sidewalks, plazas, pool decks, and entry aprons at thicknesses and finishes per civil and landscape drawings; broom finish, exposed aggregate, or scored pattern as specified.",
      "Furnish and install all asphalt paving including base course and finish course at parking lots, drive aisles, and service areas per civil drawings and paving specification.",
      "Furnish and install all unit paving including concrete pavers, clay brick pavers, and natural stone paving at pedestrian areas per landscape drawings; include all setting bed, edge restraints, and sand or mortar joints.",
      "Furnish and install all cast-in-place concrete curbs and gutters, including standard, rollover, and mountable profiles per civil drawings.",
      "Furnish and install all site retaining walls including cast-in-place, CMU, segmental block, and stone per structural and landscape drawings.",
      "Furnish and install all site stairs, ramps, and ADA-compliant pedestrian routes per civil and landscape drawings; include all required handrails and tactile warning surfaces.",
      "Furnish and install all site lighting including pole-mounted fixtures, path lights, uplights, step lights, and bollards per electrical and landscape drawings.",
      "Furnish and install all chain link, ornamental iron, wood, vinyl, and composite fencing and gates as indicated per site plans and details.",
      "Furnish and install all site drainage systems including trench drains, catch basins, area inlets, and storm piping per civil utility plan.",
      "Furnish and install all landscape planting including deciduous and evergreen trees, ornamental shrubs, groundcovers, perennials, and annuals per planting plan.",
      "Furnish and install all lawn areas including finish grading, seeding, or sod installation as specified; include soil amendments and starter fertilizer.",
      "Furnish and install all automatic irrigation system including controllers, backflow preventers, mainline, lateral pipe, heads, and drip tubing per irrigation plans.",
      "Furnish and install all site amenities including bike racks, benches, waste receptacles, picnic tables, and planters per furniture schedule.",
      "Furnish and apply all pavement markings, traffic signage, and ADA signage at parking lot and site as required by civil drawings and local code.",
    ],
    exclusions: [
      "Structural foundations for site walls and retaining structures exceeding 4 feet in height (by structural contractor; footing form and pour by this contractor if included in scope).",
      "Site electrical service and outdoor lighting panels beyond fixture connections at junction boxes provided by electrical contractor.",
      "Underground utility installation (by site utilities contractor) unless combined scope.",
      "Pool and water feature construction (by pool/aquatic contractor).",
      "Public right-of-way improvements beyond property line (by civil contractor or Owner's permit).",
      "Site security cameras and access control hardware (by security contractor).",
    ],
    referenceSpecs: [
      "ASTM C936 — Standard Specification for Solid Concrete Interlocking Paving Units",
      "ASTM C309 — Liquid Membrane-Forming Compounds for Curing Concrete",
      "AASHTO M 17 — Standard Specification for Asphalt Concrete",
      "ANSI/ASLA — American Society of Landscape Architects practice standards",
      "ANSI Z60.1 — American Standard for Nursery Stock",
      "ASTM F552 — Standard Test Methods for Chain-Link Fence Fabric",
      "ADA Standards for Accessible Design (2010 ADA Standards)",
      "MUTCD — Manual on Uniform Traffic Control Devices",
    ],
    submittals: [
      "Paving sections and mix designs (concrete and asphalt)",
      "Unit paver samples (color, texture, pattern)",
      "Planting plan and plant material schedule with nursery sources",
      "Irrigation design drawings and product data",
      "Site lighting fixture cut sheets and photometric plan",
      "Retaining wall design (PE-stamped if over 4 feet)",
      "Fencing and gate shop drawings and samples",
      "Site amenity cut sheets and finish samples",
    ],
    testingInspection: [
      "Asphalt compaction cores (minimum 2-inch thickness verification per lift)",
      "Concrete flatwork cylinder tests and slump at delivery",
      "Subbase compaction testing before paving",
      "Plant material inspection at nursery source or upon delivery",
      "Irrigation system pressure test and coverage verification",
      "Final site grading survey and ADA compliance walk",
    ],
    laborCategories: [
      "Site Foreman / Landscaper Foreman",
      "Equipment Operator (skid steer, excavator)",
      "Concrete Flatwork Finisher",
      "Asphalt Paver Operator",
      "Landscaper / Planting Laborer",
      "Irrigation Technician",
      "Truck Driver",
      "Laborer",
    ],
  },
};
